import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Languages, ChevronDown, Check } from 'lucide-react';
import { ensureLanguage } from '../i18n';

const LANGUAGES = [
  { code: 'fr', label: 'Français' },
  { code: 'en', label: 'English' },
  { code: 'ar', label: 'العربية' },
];

export function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const currentCode = (i18n.language || 'fr').substring(0, 2);

  const handleLanguageChange = (language: string) => {
    setIsOpen(false);
    const url = new URL(window.location.href);
    url.searchParams.delete('lng');

    if (language === 'fr') {
      url.searchParams.delete('lang');
    } else {
      url.searchParams.set('lang', language);
    }

    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    // Non-French dictionaries are no longer bundled: fetch this one, register it,
    // then switch. French is already in memory so it swaps instantly.
    void ensureLanguage(language).then(() => i18n.changeLanguage(language));
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <div className="relative inline-block text-start" ref={containerRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={currentCode === 'fr' ? "Sélectionner la langue" : currentCode === 'ar' ? "اختر اللغة" : "Select language"}
        className="flex items-center gap-1 sm:gap-1.5 h-8 px-2 sm:px-2.5 rounded-lg text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors border border-transparent focus-visible:border-border cursor-pointer select-none shrink-0"
      >
        <Languages className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        <span className="uppercase tracking-wider font-semibold text-[11px]">{currentCode}</span>
        <ChevronDown className={`h-3 w-3 transition-transform duration-150 shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div 
          role="listbox"
          className="absolute end-0 mt-1 w-32 rounded-xl bg-card border border-border/80 shadow-xl py-1 z-50"
        >
          {LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              role="option"
              aria-selected={lang.code === currentCode}
              type="button"
              onClick={() => handleLanguageChange(lang.code)}
              className={`w-full text-start flex items-center justify-between px-3 py-1.5 text-xs transition-colors cursor-pointer ${
                lang.code === currentCode
                  ? 'font-bold text-foreground bg-muted'
                  : 'text-foreground hover:bg-muted/60'
              }`}
            >
              <span>{lang.label}</span>
              {lang.code === currentCode && <Check className="w-3.5 h-3.5 text-primary" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
